import * as Sentry from '@sentry/react';
import type { Location } from 'history';

import env from 'env';
import { initAmplitude } from 'support/amplitude/init';
import { AuthenticationUserFragment, UserSegment } from 'types/graphql';

// Inspiration taken from: https://github.com/react-ga/react-ga/blob/v3.1.2/src/utils/loadGA.js.
function loadGoogleAnalytics() {
  (function (i: Window, s: Document, o: 'script', g: string, r: 'ga', a?: HTMLScriptElement, m?: HTMLScriptElement) {
    i['GoogleAnalyticsObject'] = r;
    i[r] =
      i[r] ||
      function () {
        (i[r].q = i[r].q || []).push(arguments);
      };
    i[r].l = Date.now();
    a = s.createElement(o);
    m = s.getElementsByTagName(o)[0];
    a.async = true;
    a.src = g;
    a.onerror = window.onerror;
    m.parentNode?.insertBefore(a, m);
  })(window, document, 'script', 'https://www.google-analytics.com/analytics.js', 'ga');

  window.ga('create', env.googleAnalyticsID, 'auto');
}

export function loadTrackers() {
  loadHubspot();
  loadHotjar();
  loadGoogleAnalytics();
}

function loadHubspot() {
  // Hubspot tracking does not work in an iframe embedded on another origin.
  // Since the cookies Hubspot sets use the `SameSite=Lax` policy, the browser
  // omits them when the application is embedded on another origin.
  if (['embed', 'arcade'].includes(window.location.pathname.split('/')[1])) {
    return;
  }

  const script = document.createElement('script');
  script.src = '//js.hs-scripts.com/4637876.js';
  script.id = 'hs-script-loader';
  script.async = true;
  script.defer = true;
  script.crossOrigin = 'anonymous';

  document.body.appendChild(script);
}

function loadHotjar() {
  (function (h: Window, o: Document, t: string, j: string, a?: HTMLHeadElement, r?: HTMLScriptElement) {
    h.hj =
      h.hj ||
      function () {
        (h.hj.q = h.hj.q || []).push(arguments);
      };
    h._hjSettings = { hjid: 2926817, hjsv: 6 };
    a = o.getElementsByTagName('head')[0];
    r = o.createElement('script');
    r.async = true;
    r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
    a.appendChild(r);
  })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
}

export function trackPageView(instruqtUser: AuthenticationUserFragment, location: Location) {
  // Google Analytics
  if (window.ga) {
    window.ga('send', {
      hitType: 'pageview',
      page: location.pathname,
    });
  }
  // Hubspot
  if (canTrackHubspot(instruqtUser)) {
    const _hsq = (window._hsq = window._hsq || []);
    _hsq.push(['setPath', location.pathname + location.search]);
    _hsq.push(['trackPageView']);
  }

  // Hotjar
  if (window.hj) {
    window.hj('stateChange', location.pathname + location.search);
  }
}

/**
 * Stores the current user in the Sentry, Amplitude, Google Analytics, and Hubspot clients.
 */
export function trackUser(instruqtUser: AuthenticationUserFragment) {
  Sentry.setUser({
    id: instruqtUser.id,
  });

  // Amplitude
  initAmplitude(instruqtUser);

  // Google Analytics
  if (window.ga) {
    // "dimension1" is a dimension with an index of 1 on Google Analytics.
    // The dimension name is "Platform Segments", and it's responsible for
    // tracking to which segments the user belongs.
    window.ga('set', 'dimension1', instruqtUser.segments.join(' ') || '');
  }

  if (canTrackHubspot(instruqtUser)) {
    const _hsq = (window._hsq = window._hsq || []);
    const details: {
      id: string;
      email?: string | null;
      platform_segments: string;
      platform_team?: string;
    } = {
      id: instruqtUser.id,
      email: instruqtUser.profile?.email,
      platform_segments: instruqtUser.segments.join(';'),
    };

    // Add a team name to Hubspot tracking, but only if the user is a part of a single team:
    if (instruqtUser.teams[0]?.node) {
      details.platform_team = instruqtUser.teams[0].node.name;
    }

    _hsq.push(['identify', details]);
  }

  // Hotjar specific tracking
  if (window.hj) {
    // Identify user segments as event in Hotjar, needed for targeting new recordings
    window.hj('event', `identify ${instruqtUser.segments.join(' ') || ''}`);

    // Track user segments in Hotjar, needed for filtering existing recordings
    window.hj('identify', instruqtUser.id, {
      user_segments: instruqtUser.segments.join(' ') || '',
    });
  }
}

export function trackAnonUser() {
  // Amplitude
  initAmplitude();

  // Google Analytics
  if (window.ga) {
    // "dimension1" is a dimension with an index of 1 on Google Analytics.
    // The dimension name is "Platform Segments", and it's responsible for
    // tracking to which segments the user belongs.
    window.ga('set', 'dimension1', 'anonymous');
  }

  // Hotjar specific tracking
  if (window.hj) {
    // Identify user segments as event in Hotjar, needed for targeting new recordings
    window.hj('event', 'identify anonymous');
  }
}

function canTrackHubspot(instruqtUser: AuthenticationUserFragment) {
  // Track only our customers (those who are part of at least one team), exclude non-admins
  return !!instruqtUser.teams.length && !instruqtUser.segments.includes(UserSegment.InstruqtEmployee);
}

declare global {
  interface Window {
    GoogleAnalyticsObject?: string;
    ga: { (...args: unknown[]): void; q?: unknown[]; l?: number };
    hj: { (...args: unknown[]): void; q?: unknown[] };
    _hjSettings: { hjid: number; hjsv: number };
    _hsq: [string, unknown?][];
  }
}
